// BulkStrategyConfigManager.jsx - Professional CRUD Interface with Monaco Editor + Visibility Control
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Stack, alpha, CircularProgress, Alert, AlertTitle,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, Divider, ToggleButtonGroup, ToggleButton, Collapse,
  Tabs, Tab, Switch, Snackbar, Skeleton
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Code as CodeIcon,
  Visibility as ViewIcon,
  VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  FilterList as FilterIcon,
  TrendingUp as LongIcon,
  TrendingDown as ShortIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  FindReplace as FindReplaceIcon,
  FormatAlignLeft as FormatIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  BarChart as TradesIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';

// ==================== CONSTANTS ====================
const API_BASE_URL = process.env.REACT_APP_API_URL || '/';

// ==================== HELPER COMPONENTS ====================
const StatusChip = ({ active, trading }) => {
  if (!active) {
    return <Chip label="INACTIVE" size="small" sx={{ bgcolor: alpha('#666', 0.2), color: '#888', fontWeight: 600, fontSize: '0.7rem' }} />;
  }
  if (!trading) {
    return <Chip label="PAUSED" size="small" sx={{ bgcolor: alpha('#f59e0b', 0.2), color: '#f59e0b', fontWeight: 600, fontSize: '0.7rem' }} />;
  }
  return <Chip label="ACTIVE" size="small" sx={{ bgcolor: alpha('#00ff88', 0.2), color: '#00ff88', fontWeight: 600, fontSize: '0.7rem' }} />;
};

const VisibilityChip = ({ visible }) => {
  if (visible === false) {
    return (
      <Chip 
        icon={<VisibilityOffIcon sx={{ fontSize: 14 }} />}
        label="HIDDEN" 
        size="small" 
        sx={{ 
          bgcolor: alpha('#ff6b6b', 0.15), 
          color: '#ff6b6b', 
          fontWeight: 600, 
          fontSize: '0.7rem',
          '& .MuiChip-icon': { color: 'inherit' }
        }} 
      />
    );
  }
  return (
    <Chip 
      icon={<ViewIcon sx={{ fontSize: 14 }} />}
      label="VISIBLE" 
      size="small" 
      sx={{ 
        bgcolor: alpha('#00d4ff', 0.15), 
        color: '#00d4ff', 
        fontWeight: 600, 
        fontSize: '0.7rem',
        '& .MuiChip-icon': { color: 'inherit' }
      }} 
    />
  );
};

const DirectionChip = ({ direction, loading }) => {
  if (loading) {
    return <Chip label="..." size="small" sx={{ bgcolor: alpha('#888', 0.15), color: '#888', fontWeight: 600, fontSize: '0.7rem' }} />;
  }
  const isLong = direction === 'LONG';
  return (
    <Chip
      icon={isLong ? <LongIcon sx={{ fontSize: 14 }} /> : <ShortIcon sx={{ fontSize: 14 }} />}
      label={direction}
      size="small"
      sx={{
        bgcolor: alpha(isLong ? '#00ff88' : '#ff4444', 0.15),
        color: isLong ? '#00ff88' : '#ff4444',
        fontWeight: 600,
        fontSize: '0.7rem',
        '& .MuiChip-icon': { color: 'inherit' }
      }}
    />
  );
};

const TimeframeChip = ({ timeframe }) => (
  <Chip
    label={timeframe}
    size="small"
    sx={{
      bgcolor: alpha('#00d4ff', 0.15),
      color: '#00d4ff',
      fontWeight: 600,
      fontSize: '0.7rem',
    }}
  />
);

// ==================== MONACO EDITOR COMPONENT ====================
const MonacoJsonEditor = ({ value, onChange, readOnly = false, error = null }) => {
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editorReady, setEditorReady] = useState(false);

  const stringValue = useMemo(() => {
    try {
      if (value === null || value === undefined) return '{}';
      return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
    } catch (e) {
      return '{}';
    }
  }, [value]);

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    setEditorReady(true);
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      allowComments: false,
      schemas: [],
      enableSchemaRequest: false,
    });
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      formatDocument();
    });
    editor.focus();
    setTimeout(() => { editor.layout(); }, 100);
  };

  const handleEditorChange = (newValue) => {
    if (!newValue) return;
    try {
      const parsed = JSON.parse(newValue);
      setParseError(null);
      if (onChange) onChange(parsed);
    } catch (e) {
      setParseError(e.message);
    }
  };

  const formatDocument = () => {
    if (editorRef.current) {
      editorRef.current.getAction('editor.action.formatDocument').run();
    }
  };

  const openFind = () => {
    if (editorRef.current) {
      editorRef.current.getAction('actions.find').run();
    }
  };

  const openFindReplace = () => {
    if (editorRef.current) {
      editorRef.current.getAction('editor.action.startFindReplaceAction').run();
    }
  };

  const copyToClipboard = async () => {
    try {
      const content = editorRef.current?.getValue() || stringValue;
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('Failed to copy:', e);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  useEffect(() => {
    if (editorRef.current) {
      setTimeout(() => { editorRef.current.layout(); }, 50);
    }
  }, [isFullscreen]);

  const editorOptions = {
    readOnly,
    minimap: { enabled: true, scale: 1, showSlider: 'mouseover' },
    fontSize: 14,
    fontFamily: '"Fira Code", "Cascadia Code", "JetBrains Mono", "SF Mono", Consolas, monospace',
    fontLigatures: true,
    lineNumbers: 'on',
    lineNumbersMinChars: 4,
    renderLineHighlight: 'all',
    scrollBeyondLastLine: false,
    automaticLayout: true,
    tabSize: 2,
    wordWrap: 'on',
    wrappingStrategy: 'advanced',
    formatOnPaste: true,
    formatOnType: true,
    scrollbar: {
      vertical: 'visible',
      horizontal: 'visible',
      useShadows: true,
      verticalScrollbarSize: 12,
      horizontalScrollbarSize: 12,
    },
    suggest: { showKeywords: true, showSnippets: true },
    quickSuggestions: true,
    folding: true,
    foldingStrategy: 'indentation',
    showFoldingControls: 'always',
    bracketPairColorization: { enabled: true },
    guides: {
      bracketPairs: true,
      indentation: true,
      highlightActiveBracketPair: true,
      highlightActiveIndentation: true,
    },
    cursorBlinking: 'smooth',
    cursorSmoothCaretAnimation: 'on',
    cursorStyle: 'line',
    smoothScrolling: true,
    mouseWheelZoom: true,
    contextmenu: true,
    find: {
      addExtraSpaceOnTop: true,
      autoFindInSelection: 'multiline',
      seedSearchStringFromSelection: 'selection',
    },
    padding: { top: 10, bottom: 10 },
    renderWhitespace: 'selection',
    colorDecorators: true,
    linkedEditing: true,
  };

  const fullscreenStyles = isFullscreen ? {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 9999,
    bgcolor: '#1e1e1e',
    display: 'flex',
    flexDirection: 'column',
  } : {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 400,
  };

  return (
    <Box ref={containerRef} sx={fullscreenStyles}>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 1.5, py: 0.75, bgcolor: '#323233',
        borderBottom: '1px solid #3c3c3c',
        borderRadius: isFullscreen ? 0 : '8px 8px 0 0',
        flexShrink: 0,
      }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="Format Document (Ctrl+Shift+F)">
            <IconButton size="small" onClick={formatDocument} disabled={readOnly} sx={{ color: '#cccccc', '&:hover': { bgcolor: '#505050' } }}>
              <FormatIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, bgcolor: '#505050' }} />
          <Tooltip title="Find (Ctrl+F)">
            <IconButton size="small" onClick={openFind} sx={{ color: '#cccccc', '&:hover': { bgcolor: '#505050' } }}>
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Find and Replace (Ctrl+H)">
            <IconButton size="small" onClick={openFindReplace} disabled={readOnly} sx={{ color: '#cccccc', '&:hover': { bgcolor: '#505050' } }}>
              <FindReplaceIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, bgcolor: '#505050' }} />
          <Tooltip title={copied ? 'Copied!' : 'Copy to Clipboard'}>
            <IconButton size="small" onClick={copyToClipboard} sx={{ color: copied ? '#00ff88' : '#cccccc', '&:hover': { bgcolor: '#505050' } }}>
              {copied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {parseError && (
            <Chip
              icon={<ErrorIcon sx={{ fontSize: 14 }} />}
              label="Invalid JSON"
              size="small"
              sx={{ bgcolor: alpha('#ff4444', 0.2), color: '#f48771', fontSize: '0.7rem', height: 24 }}
            />
          )}
          {!parseError && !readOnly && (
            <Chip
              icon={<CheckIcon sx={{ fontSize: 14 }} />}
              label="Valid JSON"
              size="small"
              sx={{ bgcolor: alpha('#00ff88', 0.15), color: '#89d185', fontSize: '0.7rem', height: 24 }}
            />
          )}
          {readOnly && (
            <Chip label="READ ONLY" size="small" sx={{ bgcolor: alpha('#f59e0b', 0.2), color: '#f59e0b', fontSize: '0.7rem', height: 24 }} />
          )}
          <Tooltip title={isFullscreen ? 'Exit Fullscreen (Esc)' : 'Fullscreen'}>
            <IconButton size="small" onClick={toggleFullscreen} sx={{ color: '#cccccc', '&:hover': { bgcolor: '#505050' } }}>
              {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      <Box sx={{
        flex: 1, minHeight: 0,
        border: '1px solid #3c3c3c', borderTop: 'none', borderBottom: 'none',
        overflow: 'hidden', bgcolor: '#1e1e1e',
      }}>
        <Editor
          height="100%"
          width="100%"
          defaultLanguage="json"
          value={stringValue}
          onChange={handleEditorChange}
          onMount={handleEditorDidMount}
          theme="vs-dark"
          options={editorOptions}
          loading={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', bgcolor: '#1e1e1e' }}>
              <CircularProgress size={32} sx={{ color: '#00d4ff' }} />
              <Typography sx={{ ml: 2, color: '#888' }}>Loading editor...</Typography>
            </Box>
          }
        />
      </Box>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 1.5, py: 0.25, bgcolor: '#007acc',
        borderRadius: isFullscreen ? 0 : '0 0 8px 8px',
        fontSize: '0.75rem', color: '#fff', minHeight: 24, flexShrink: 0,
      }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <span style={{ fontWeight: 500 }}>JSON</span>
          <span>UTF-8</span>
          <span>LF</span>
        </Stack>
        <Stack direction="row" spacing={3} alignItems="center">
          <Typography variant="caption" sx={{ opacity: 0.9 }}>Ctrl+F: Find</Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>Ctrl+H: Replace</Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>Ctrl+S: Format</Typography>
        </Stack>
      </Box>
      {(parseError || error) && (
        <Alert 
          severity="error" 
          sx={{ 
            mt: 1, bgcolor: alpha('#ff4444', 0.1),
            border: `1px solid ${alpha('#ff4444', 0.3)}`,
            '& .MuiAlert-message': { fontFamily: 'monospace', fontSize: '0.8rem' },
            flexShrink: 0,
          }}
        >
          {parseError || error}
        </Alert>
      )}
    </Box>
  );
};

// ==================== STRATEGY DETAIL DIALOG ====================
const StrategyDetailDialog = ({ open, onClose, strategy, onSave, onDelete, mode = 'view' }) => {
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(mode === 'edit' || mode === 'create');
  const [configData, setConfigData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (strategy?.strategy_config) {
      try {
        const parsed = typeof strategy.strategy_config === 'string' 
          ? JSON.parse(strategy.strategy_config) 
          : strategy.strategy_config;
        setConfigData(parsed);
      } catch (e) {
        setConfigData(strategy.strategy_config);
      }
    } else if (mode === 'create') {
      setConfigData({
        bulk_strategy_id: '',
        symbol: 'NQ',
        timeframe: '1m',
        trading_hour: 'RTH',
        direction: 'LONG',
        description: '',
        sub_strategies: []
      });
    }
    setEditMode(mode === 'edit' || mode === 'create');
    setError(null);
  }, [strategy, mode, open]);

  const handleSave = async () => {
    if (!configData) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(configData, mode === 'create');
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to save strategy');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!strategy?.bulk_strategy_id) return;
    setSaving(true);
    try {
      await onDelete(strategy.bulk_strategy_id);
      setDeleteConfirm(false);
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to delete strategy');
    } finally {
      setSaving(false);
    }
  };

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(configData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${configData?.bulk_strategy_id || 'strategy'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#1e1e1e', backgroundImage: 'none',
            border: `1px solid #3c3c3c`,
            height: '90vh', maxHeight: '90vh',
            display: 'flex', flexDirection: 'column',
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid #3c3c3c',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          bgcolor: '#252526', py: 1.5, flexShrink: 0,
        }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <CodeIcon sx={{ color: '#00d4ff' }} />
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
              {mode === 'create' ? 'Create New Strategy' : (strategy?.bulk_strategy_id || 'Strategy Details')}
            </Typography>
            {strategy && (
              <>
                <StatusChip active={strategy.is_active} trading={strategy.trading_enabled} />
                <VisibilityChip visible={strategy.is_visible} />
                {configData?.direction && <DirectionChip direction={configData.direction} />}
              </>
            )}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {!editMode && mode !== 'create' && (
              <Button startIcon={<EditIcon />} onClick={() => setEditMode(true)} sx={{ color: '#00d4ff' }}>
                Edit
              </Button>
            )}
            {mode !== 'create' && strategy?.bulk_strategy_id && (
              <Tooltip title="View Trades">
                <IconButton
                  onClick={() => {
                    onClose();
                    navigate(`/officer/trade-history?strategy=${encodeURIComponent(strategy.bulk_strategy_id)}`);
                  }}
                  sx={{ color: '#10b981', '&:hover': { bgcolor: alpha('#10b981', 0.1) } }}
                >
                  <TradesIcon />
                </IconButton>
              </Tooltip>
            )}
            <IconButton onClick={onClose} sx={{ color: 'grey.500' }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <Box sx={{ borderBottom: '1px solid #3c3c3c', bgcolor: '#252526', flexShrink: 0 }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, v) => setActiveTab(v)}
            sx={{ 
              minHeight: 40,
              '& .MuiTab-root': { color: 'grey.500', minHeight: 40, py: 1 },
              '& .Mui-selected': { color: '#00d4ff' },
            }}
          >
            <Tab label="JSON Configuration" />
            <Tab label="Strategy Info" disabled={mode === 'create'} />
          </Tabs>
        </Box>

        <DialogContent sx={{ p: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {activeTab === 0 && (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <MonacoJsonEditor 
                value={configData} 
                onChange={setConfigData}
                readOnly={!editMode}
                error={error}
              />
            </Box>
          )}
          {activeTab === 1 && strategy && (
            <Stack spacing={2} sx={{ overflow: 'auto' }}>
              <Paper sx={{ p: 2, bgcolor: alpha('#00d4ff', 0.05), border: `1px solid ${alpha('#00d4ff', 0.1)}` }}>
                <Typography variant="subtitle2" sx={{ color: '#00d4ff', mb: 1 }}>Metadata</Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ color: 'grey.500' }}>ID:</Typography>
                    <Typography sx={{ color: '#fff', fontFamily: 'monospace' }}>{strategy.id}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ color: 'grey.500' }}>Version:</Typography>
                    <Typography sx={{ color: '#fff' }}>v{strategy.version}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ color: 'grey.500' }}>Visibility:</Typography>
                    <VisibilityChip visible={strategy.is_visible} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ color: 'grey.500' }}>Created:</Typography>
                    <Typography sx={{ color: '#fff' }}>{new Date(strategy.created_at).toLocaleString()}</Typography>
                  </Box>
                  {strategy.updated_at && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ color: 'grey.500' }}>Updated:</Typography>
                      <Typography sx={{ color: '#fff' }}>{new Date(strategy.updated_at).toLocaleString()}</Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Stack>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2, bgcolor: alpha('#ff4444', 0.1), flexShrink: 0 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          borderTop: '1px solid #3c3c3c', bgcolor: '#252526',
          p: 2, justifyContent: 'space-between', flexShrink: 0,
        }}>
          <Stack direction="row" spacing={1}>
            {mode !== 'create' && (
              <>
                <Button startIcon={<DownloadIcon />} onClick={downloadJson} sx={{ color: 'grey.400' }}>
                  Export JSON
                </Button>
                <Button startIcon={<DeleteIcon />} onClick={() => setDeleteConfirm(true)} sx={{ color: '#ff4444' }}>
                  Delete
                </Button>
              </>
            )}
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button onClick={onClose} sx={{ color: 'grey.400' }}>Cancel</Button>
            {editMode && (
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{ bgcolor: '#00d4ff', color: '#000', fontWeight: 600, '&:hover': { bgcolor: '#00b8d9' } }}
              >
                {mode === 'create' ? 'Create Strategy' : 'Save Changes'}
              </Button>
            )}
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirm}
        onClose={() => setDeleteConfirm(false)}
        PaperProps={{ sx: { bgcolor: '#1e1e1e', border: `1px solid ${alpha('#ff4444', 0.3)}` } }}
      >
        <DialogTitle sx={{ color: '#ff4444' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningIcon />
            <span>Confirm Delete</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'grey.300' }}>
            Are you sure you want to permanently delete <strong style={{ color: '#fff' }}>{strategy?.bulk_strategy_id}</strong>?
          </Typography>
          <Typography sx={{ color: 'grey.500', mt: 1, fontSize: '0.85rem' }}>
            This will also delete all trade history and state data for this strategy.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(false)} sx={{ color: 'grey.400' }}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <DeleteIcon />}
            onClick={handleDelete}
            disabled={saving}
            sx={{ bgcolor: '#ff4444', '&:hover': { bgcolor: '#cc0000' } }}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ==================== MAIN COMPONENT ====================
const BulkStrategyConfigManager = ({ apiBaseUrl = API_BASE_URL }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false); // background enrichment indicator
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    timeframe: 'all',
    direction: 'all',
    status: 'all',
    tradingHour: 'all',
    visibility: 'all',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('view');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [refreshing, setRefreshing] = useState(false);

  // ---------------------------------------------------------------------------
  // PHASE 1: Render immediately from the list endpoint (single request).
  // PHASE 2: Enrich direction + is_visible per-strategy in the background so
  //          the table is visible within ~1 request RTT instead of N+1.
  //
  // NOTE FOR BACKEND TEAM: Add `direction` and `is_visible` to the list
  // endpoint response (/strategies/api/config/list/) to eliminate Phase 2
  // entirely and remove the N individual config fetches.
  // ---------------------------------------------------------------------------
  const fetchStrategies = useCallback(async () => {
    try {
      setError(null);

      // ── Phase 1: single list call → render immediately ──────────────────────
      const response = await axios.get(`${apiBaseUrl}/strategies/api/config/list/`);

      if (!response.data.success) {
        setError(response.data.error || 'Failed to load strategies');
        return;
      }

      // Render right away with whatever the list gives us.
      // direction defaults to 'LONG' and is_visible defaults to true until
      // the background enrichment fills in the real values.
      const initial = response.data.strategies.map((s) => ({
        ...s,
        direction: s.direction || null,   // null = "not yet enriched"
        is_visible: s.is_visible ?? true,
        _enriched: !!(s.direction),       // skip re-fetch if list already has it
      }));

      setStrategies(initial);
      setLoading(false);

      // If the list endpoint already returned direction for every strategy,
      // there's nothing to enrich — bail out early.
      const needsEnrichment = initial.filter((s) => !s._enriched);
      if (needsEnrichment.length === 0) return;

      // ── Phase 2: enrich direction + is_visible in background ────────────────
      // Fire all detail requests in parallel (Promise.allSettled so one failure
      // doesn't block the rest) but don't block the initial render on them.
      setEnriching(true);

      const BATCH_SIZE = 10; // avoid overwhelming the server / browser
      for (let i = 0; i < needsEnrichment.length; i += BATCH_SIZE) {
        const batch = needsEnrichment.slice(i, i + BATCH_SIZE);

        const results = await Promise.allSettled(
          batch.map((s) =>
            axios.get(`${apiBaseUrl}/strategies/api/config/${s.bulk_strategy_id}/`)
          )
        );

        setStrategies((prev) => {
          const updated = [...prev];
          results.forEach((result, idx) => {
            if (result.status !== 'fulfilled' || !result.value.data.success) return;
            const detail = result.value.data.strategy;
            const stratId = batch[idx].bulk_strategy_id;
            const pos = updated.findIndex((s) => s.bulk_strategy_id === stratId);
            if (pos === -1) return;
            try {
              const config =
                typeof detail.strategy_config === 'string'
                  ? JSON.parse(detail.strategy_config)
                  : detail.strategy_config;
              updated[pos] = {
                ...updated[pos],
                direction: config.direction || 'LONG',
                is_visible: detail.is_visible ?? true,
                strategy_config: detail.strategy_config,
                _enriched: true,
              };
            } catch {
              updated[pos] = { ...updated[pos], _enriched: true };
            }
          });
          return updated;
        });
      }
    } catch (e) {
      setError(e.message || 'Failed to load strategies');
    } finally {
      setLoading(false);
      setRefreshing(false);
      setEnriching(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    fetchStrategies();
  }, [fetchStrategies]);

  // Auto-open strategy from ?open= query param (e.g. navigated from Trade History)
  const openParam = searchParams.get('open');
  useEffect(() => {
    if (!openParam || strategies.length === 0) return;
    const match = strategies.find((s) => s.bulk_strategy_id === openParam);
    if (match) handleOpenStrategy(match, 'view');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openParam, strategies]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStrategies();
  };

  const filterOptions = useMemo(() => {
    const timeframes = [...new Set(strategies.map((s) => s.timeframe))].filter(Boolean).sort();
    const tradingHours = [...new Set(strategies.map((s) => s.trading_hour))].filter(Boolean).sort();
    return { timeframes, tradingHours };
  }, [strategies]);

  const visibilityCounts = useMemo(() => {
    const visible = strategies.filter((s) => s.is_visible !== false).length;
    const hidden = strategies.filter((s) => s.is_visible === false).length;
    return { visible, hidden };
  }, [strategies]);

  const filteredStrategies = useMemo(() => {
    return strategies.filter((s) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (
          !s.bulk_strategy_id.toLowerCase().includes(query) &&
          !s.description?.toLowerCase().includes(query)
        ) return false;
      }
      if (filters.timeframe !== 'all' && s.timeframe !== filters.timeframe) return false;
      if (filters.tradingHour !== 'all' && s.trading_hour !== filters.tradingHour) return false;
      // Skip direction filter for un-enriched rows so they stay visible
      if (filters.direction !== 'all' && s.direction && s.direction !== filters.direction) return false;
      if (filters.status === 'active' && (!s.is_active || !s.trading_enabled)) return false;
      if (filters.status === 'paused' && (!s.is_active || s.trading_enabled)) return false;
      if (filters.status === 'inactive' && s.is_active) return false;
      if (filters.visibility === 'visible' && s.is_visible === false) return false;
      if (filters.visibility === 'hidden' && s.is_visible !== false) return false;
      return true;
    });
  }, [strategies, searchQuery, filters]);

  const handleOpenStrategy = async (strategy, mode = 'view') => {
    try {
      // If we already have the full config from enrichment, use it directly.
      if (strategy.strategy_config) {
        setSelectedStrategy(strategy);
        setDialogMode(mode);
        setDialogOpen(true);
        return;
      }
      // Fallback: fetch on-demand (first-time open before enrichment completes).
      const response = await axios.get(`${apiBaseUrl}/strategies/api/config/${strategy.bulk_strategy_id}/`);
      if (response.data.success) {
        setSelectedStrategy(response.data.strategy);
        setDialogMode(mode);
        setDialogOpen(true);
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to load strategy details', severity: 'error' });
    }
  };

  const handleCreateNew = () => {
    setSelectedStrategy(null);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const handleSaveStrategy = async (configData, isNew = false) => {
    const url = isNew
      ? `${apiBaseUrl}/strategies/api/config/create/`
      : `${apiBaseUrl}/strategies/api/config/${configData.bulk_strategy_id}/update/`;
    const method = isNew ? 'post' : 'put';
    const response = await axios[method](url, configData);
    if (response.data.success) {
      setSnackbar({
        open: true,
        message: isNew ? 'Strategy created successfully' : 'Strategy updated successfully',
        severity: 'success',
      });
      fetchStrategies();
    } else {
      throw new Error(response.data.error || 'Failed to save strategy');
    }
  };

  const handleDeleteStrategy = async (bulkStrategyId) => {
    const response = await axios.delete(`${apiBaseUrl}/strategies/api/config/${bulkStrategyId}/delete/`);
    if (response.data.success) {
      setSnackbar({ open: true, message: 'Strategy deleted successfully', severity: 'success' });
      fetchStrategies();
    } else {
      throw new Error(response.data.error || 'Failed to delete strategy');
    }
  };

  const handleToggleTrading = async (strategy, e) => {
    e.stopPropagation();
    const newState = !strategy.trading_enabled;
    try {
      const response = await axios.post(
        `${apiBaseUrl}/strategies/api/config/${strategy.bulk_strategy_id}/toggle/?enabled=${newState}`
      );
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: `Trading ${newState ? 'enabled' : 'disabled'} for ${strategy.bulk_strategy_id}`,
          severity: 'success',
        });
        fetchStrategies();
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to toggle trading', severity: 'error' });
    }
  };

  const handleToggleVisibility = async (strategy, e) => {
    e.stopPropagation();
    const newState = strategy.is_visible === false ? true : false;
    setStrategies((prev) =>
      prev.map((s) =>
        s.bulk_strategy_id === strategy.bulk_strategy_id ? { ...s, is_visible: newState } : s
      )
    );
    setSnackbar({
      open: true,
      message: `${strategy.bulk_strategy_id} is now ${newState ? 'visible' : 'hidden'}`,
      severity: 'success',
    });
    try {
      await axios.post(
        `${apiBaseUrl}/strategies/api/config/${strategy.bulk_strategy_id}/visibility/?visible=${newState}`
      );
    } catch (e) {
      try {
        await axios.put(`${apiBaseUrl}/strategies/api/config/${strategy.bulk_strategy_id}/update-field/`, {
          field: 'is_visible',
          value: newState,
        });
      } catch (fallbackError) {
        setStrategies((prev) =>
          prev.map((s) =>
            s.bulk_strategy_id === strategy.bulk_strategy_id ? { ...s, is_visible: !newState } : s
          )
        );
        setSnackbar({
          open: true,
          message: 'Failed to save visibility. Please add the backend endpoint.',
          severity: 'error',
        });
      }
    }
  };

  const handleImportJson = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const config = JSON.parse(event.target.result);
          setSelectedStrategy({ strategy_config: JSON.stringify(config) });
          setDialogMode('create');
          setDialogOpen(true);
        } catch (err) {
          setSnackbar({ open: true, message: 'Invalid JSON file', severity: 'error' });
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={2}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rectangular" height={60} sx={{ bgcolor: alpha('#00d4ff', 0.1), borderRadius: 1 }} />
          ))}
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, minHeight: '100vh', bgcolor: '#0f1419' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3, mb: 3,
          background: `linear-gradient(135deg, ${alpha('#00d4ff', 0.1)} 0%, ${alpha('#764ba2', 0.1)} 100%)`,
          border: `1px solid ${alpha('#00d4ff', 0.2)}`,
          borderRadius: 2,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2}>
          <Box>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon sx={{ color: '#00d4ff' }} />
              Strategy Configuration Manager
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Typography sx={{ color: 'grey.500' }}>
                {strategies.length} strategies configured • {filteredStrategies.length} shown •{' '}
                <Box component="span" sx={{ color: '#00d4ff' }}>{visibilityCounts.visible} visible</Box> •{' '}
                <Box component="span" sx={{ color: '#ff6b6b' }}>{visibilityCounts.hidden} hidden</Box>
              </Typography>
              {/* Unobtrusive background-enrichment indicator */}
              {enriching && (
                <Tooltip title="Loading strategy details in background...">
                  <CircularProgress size={14} sx={{ color: alpha('#00d4ff', 0.5) }} />
                </Tooltip>
              )}
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<UploadIcon />}
              onClick={handleImportJson}
              sx={{ color: 'grey.400', borderColor: alpha('#666', 0.3) }}
              variant="outlined"
            >
              Import
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
              sx={{ bgcolor: '#00d4ff', color: '#000', fontWeight: 600, '&:hover': { bgcolor: '#00b8d9' } }}
            >
              New Strategy
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3, bgcolor: alpha('#ff4444', 0.1) }} onClose={() => setError(null)}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      {/* Search and Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 2, mb: 3,
          bgcolor: alpha('#0a0e14', 0.8),
          border: `1px solid ${alpha('#00d4ff', 0.1)}`,
          borderRadius: 2,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            placeholder="Search strategies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                bgcolor: '#0f1419',
                '& fieldset': { borderColor: alpha('#00d4ff', 0.2) },
              },
              '& input': { color: '#fff' }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'grey.500' }} />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{ color: showFilters ? '#00d4ff' : 'grey.400' }}
            >
              Filters
            </Button>
            <IconButton onClick={handleRefresh} sx={{ color: 'grey.400' }}>
              <RefreshIcon sx={{
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } }
              }} />
            </IconButton>
          </Stack>
        </Stack>

        <Collapse in={showFilters}>
          <Divider sx={{ my: 2, borderColor: alpha('#00d4ff', 0.1) }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
            <Box>
              <Typography variant="caption" sx={{ color: 'grey.500', mb: 0.5, display: 'block' }}>Timeframe</Typography>
              <ToggleButtonGroup value={filters.timeframe} exclusive onChange={(e, v) => v && setFilters((f) => ({ ...f, timeframe: v }))} size="small">
                <ToggleButton value="all" sx={{ color: 'grey.400', '&.Mui-selected': { color: '#00d4ff', bgcolor: alpha('#00d4ff', 0.1) } }}>All</ToggleButton>
                {filterOptions.timeframes.map((tf) => (
                  <ToggleButton key={tf} value={tf} sx={{ color: 'grey.400', '&.Mui-selected': { color: '#00d4ff', bgcolor: alpha('#00d4ff', 0.1) } }}>{tf}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'grey.500', mb: 0.5, display: 'block' }}>Direction</Typography>
              <ToggleButtonGroup value={filters.direction} exclusive onChange={(e, v) => v && setFilters((f) => ({ ...f, direction: v }))} size="small">
                <ToggleButton value="all" sx={{ color: 'grey.400', '&.Mui-selected': { color: '#00d4ff', bgcolor: alpha('#00d4ff', 0.1) } }}>All</ToggleButton>
                <ToggleButton value="LONG" sx={{ color: 'grey.400', '&.Mui-selected': { color: '#00ff88', bgcolor: alpha('#00ff88', 0.1) } }}>Long</ToggleButton>
                <ToggleButton value="SHORT" sx={{ color: 'grey.400', '&.Mui-selected': { color: '#ff4444', bgcolor: alpha('#ff4444', 0.1) } }}>Short</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'grey.500', mb: 0.5, display: 'block' }}>Status</Typography>
              <ToggleButtonGroup value={filters.status} exclusive onChange={(e, v) => v && setFilters((f) => ({ ...f, status: v }))} size="small">
                <ToggleButton value="all" sx={{ color: 'grey.400', '&.Mui-selected': { color: '#00d4ff', bgcolor: alpha('#00d4ff', 0.1) } }}>All</ToggleButton>
                <ToggleButton value="active" sx={{ color: 'grey.400', '&.Mui-selected': { color: '#00ff88', bgcolor: alpha('#00ff88', 0.1) } }}>Active</ToggleButton>
                <ToggleButton value="paused" sx={{ color: 'grey.400', '&.Mui-selected': { color: '#f59e0b', bgcolor: alpha('#f59e0b', 0.1) } }}>Paused</ToggleButton>
                <ToggleButton value="inactive" sx={{ color: 'grey.400', '&.Mui-selected': { color: '#666', bgcolor: alpha('#666', 0.1) } }}>Inactive</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'grey.500', mb: 0.5, display: 'block' }}>Trading Hour</Typography>
              <ToggleButtonGroup value={filters.tradingHour} exclusive onChange={(e, v) => v && setFilters((f) => ({ ...f, tradingHour: v }))} size="small">
                <ToggleButton value="all" sx={{ color: 'grey.400', '&.Mui-selected': { color: '#00d4ff', bgcolor: alpha('#00d4ff', 0.1) } }}>All</ToggleButton>
                {filterOptions.tradingHours.map((th) => (
                  <ToggleButton key={th} value={th} sx={{ color: 'grey.400', '&.Mui-selected': { color: '#00d4ff', bgcolor: alpha('#00d4ff', 0.1) } }}>{th}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'grey.500', mb: 0.5, display: 'block' }}>Visibility</Typography>
              <ToggleButtonGroup value={filters.visibility} exclusive onChange={(e, v) => v && setFilters((f) => ({ ...f, visibility: v }))} size="small">
                <ToggleButton value="all" sx={{ color: 'grey.400', '&.Mui-selected': { color: '#00d4ff', bgcolor: alpha('#00d4ff', 0.1) } }}>
                  All ({strategies.length})
                </ToggleButton>
                <ToggleButton value="visible" sx={{ color: 'grey.400', '&.Mui-selected': { color: '#00d4ff', bgcolor: alpha('#00d4ff', 0.1) } }}>
                  <ViewIcon sx={{ fontSize: 16, mr: 0.5 }} />Visible ({visibilityCounts.visible})
                </ToggleButton>
                <ToggleButton value="hidden" sx={{ color: 'grey.400', '&.Mui-selected': { color: '#ff6b6b', bgcolor: alpha('#ff6b6b', 0.1) } }}>
                  <VisibilityOffIcon sx={{ fontSize: 16, mr: 0.5 }} />Hidden ({visibilityCounts.hidden})
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Stack>
        </Collapse>
      </Paper>

      {/* Strategy Table */}
      <TableContainer component={Paper} elevation={0} sx={{ bgcolor: alpha('#0a0e14', 0.8), border: `1px solid ${alpha('#00d4ff', 0.1)}`, borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha('#00d4ff', 0.05) }}>
              <TableCell sx={{ color: '#00d4ff', fontWeight: 600 }}>Strategy ID</TableCell>
              <TableCell sx={{ color: '#00d4ff', fontWeight: 600 }}>Timeframe</TableCell>
              <TableCell sx={{ color: '#00d4ff', fontWeight: 600 }}>Direction</TableCell>
              <TableCell sx={{ color: '#00d4ff', fontWeight: 600 }}>Trading Hour</TableCell>
              <TableCell sx={{ color: '#00d4ff', fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ color: '#00d4ff', fontWeight: 600 }}>Version</TableCell>
              <TableCell sx={{ color: '#00d4ff', fontWeight: 600 }} align="center">Trading</TableCell>
              <TableCell sx={{ color: '#00d4ff', fontWeight: 600 }} align="center">Visible</TableCell>
              <TableCell sx={{ color: '#00d4ff', fontWeight: 600 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredStrategies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <Typography sx={{ color: 'grey.500' }}>No strategies found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredStrategies.map((strategy) => (
                <TableRow
                  key={strategy.bulk_strategy_id}
                  hover
                  onClick={() => handleOpenStrategy(strategy)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha('#00d4ff', 0.05) },
                    opacity: strategy.is_visible === false ? 0.65 : 1,
                    bgcolor: strategy.is_visible === false ? alpha('#ff6b6b', 0.02) : 'transparent',
                  }}
                >
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {strategy.is_visible === false && (
                        <Tooltip title="Hidden from dashboard">
                          <VisibilityOffIcon sx={{ fontSize: 16, color: '#ff6b6b' }} />
                        </Tooltip>
                      )}
                      <Box>
                        <Typography sx={{ color: '#fff', fontWeight: 600, fontFamily: 'monospace' }}>
                          {strategy.bulk_strategy_id}
                        </Typography>
                        {strategy.description && (
                          <Typography sx={{ color: 'grey.500', fontSize: '0.75rem', mt: 0.5 }}>
                            {strategy.description.substring(0, 50)}{strategy.description.length > 50 ? '...' : ''}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell><TimeframeChip timeframe={strategy.timeframe} /></TableCell>
                  <TableCell>
                    {/* Shows a placeholder "..." chip while enrichment is in flight */}
                    <DirectionChip direction={strategy.direction || 'LONG'} loading={!strategy._enriched && !strategy.direction} />
                  </TableCell>
                  <TableCell>
                    <Chip label={strategy.trading_hour} size="small" sx={{ bgcolor: alpha('#764ba2', 0.2), color: '#a78bfa', fontWeight: 600, fontSize: '0.7rem' }} />
                  </TableCell>
                  <TableCell><StatusChip active={strategy.is_active} trading={strategy.trading_enabled} /></TableCell>
                  <TableCell><Typography sx={{ color: 'grey.400' }}>v{strategy.version}</Typography></TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Switch
                      checked={strategy.trading_enabled}
                      onChange={(e) => handleToggleTrading(strategy, e)}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#00ff88' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: alpha('#00ff88', 0.3) },
                      }}
                    />
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={strategy.is_visible === false ? 'Click to show in dashboard' : 'Click to hide from dashboard'}>
                      <Switch
                        checked={strategy.is_visible !== false}
                        onChange={(e) => handleToggleVisibility(strategy, e)}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#00d4ff' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: alpha('#00d4ff', 0.3) },
                          '& .MuiSwitch-switchBase': { color: '#ff6b6b' },
                          '& .MuiSwitch-track': { bgcolor: alpha('#ff6b6b', 0.3) },
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="View trades">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/officer/trade-history?strategy=${encodeURIComponent(strategy.bulk_strategy_id)}`)}
                          sx={{ color: '#10b981', '&:hover': { bgcolor: alpha('#10b981', 0.1) } }}
                        >
                          <TradesIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View">
                        <IconButton size="small" onClick={() => handleOpenStrategy(strategy, 'view')} sx={{ color: 'grey.400' }}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenStrategy(strategy, 'edit')} sx={{ color: '#00d4ff' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <StrategyDetailDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        strategy={selectedStrategy}
        mode={dialogMode}
        onSave={handleSaveStrategy}
        onDelete={handleDeleteStrategy}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{
            bgcolor: snackbar.severity === 'success' ? alpha('#00ff88', 0.1) : alpha('#ff4444', 0.1),
            border: `1px solid ${snackbar.severity === 'success' ? alpha('#00ff88', 0.3) : alpha('#ff4444', 0.3)}`,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BulkStrategyConfigManager;
